<script setup lang="ts">
import {
  EntityOfferHomePagePrimaryCardCard,
  EntityOfferHomePagePrimaryCardCredit,
  EntityOfferHomePagePrimaryCardDeposit
} from '~/components/entity/offer/home-page'
import { WidgetHomePageBestOffersSkeleton } from '~/components/widget/best-offers'
import { Swiper, SwiperSlide } from 'swiper/vue'
import type {
  IBestOfferDeposit,
  IBestOfferCredit,
  IBestOfferCard
} from '~/components/widget/best-offers/types'
interface IRelatedProduct extends Omit<
  IBestOfferDeposit & IBestOfferCredit & IBestOfferCard,
  'type'
> {
  id: number
  title: string
  url: string
  related_type: 'card' | 'credit' | 'deposit'
}

interface Props {
  products: IRelatedProduct[]
  status: string
}

defineProps<Props>()

const { t } = useI18n()

const getPrimaryCardComponent = (type: string) => {
  if (type === 'deposit') return EntityOfferHomePagePrimaryCardDeposit
  if (type === 'credit') return EntityOfferHomePagePrimaryCardCredit
  if (type === 'card') return EntityOfferHomePagePrimaryCardCard
  return null
}

const mapItemByType = (item: IRelatedProduct) => {
  if (item.related_type === 'deposit') {
    const data = item as IRelatedProduct & IBestOfferDeposit
    return {
      title: data.title,
      rate: t('widget.related_products.deposit_rate', { rate: data.rate }),
      period: t('widget.related_products.period', { period: data.period }),
      companyName: data.organization.title,
      companyLogo: data.organization.logo,
      url: data.url
    }
  }

  if (item.related_type === 'credit') {
    const data = item as IRelatedProduct & IBestOfferCredit
    return {
      title: data.credit.title,
      rate: t('widget.related_products.credit_rate', { rate: data.rate }),
      period: t('widget.related_products.period', { period: data.period }),
      companyName: data.credit.organization.title,
      companyLogo: data.credit.organization.logo,
      url: data.credit.url
    }
  }

  if (item.related_type === 'card') {
    const data = item as IRelatedProduct & IBestOfferCard
    return {
      title: '',
      currency_type: data.currency_type,
      currencies: data.currencies,
      rate: data.title,
      period:
        data.issue_fee === 0
          ? t('widget.related_products.free_issue')
          : t('widget.related_products.issue_fee', { fee: data.issue_fee }),
      companyName: data.organization.title,
      companyLogo: data.organization.logo,
      url: data.url
    }
  }

  return item
}
</script>

<template>
  <section class="py-3">
    <header class="mb-3 flex items-center justify-between gap-4">
      <Heading tag="h2" class="h3">{{ $t('widget.related_products.heading') }}</Heading>
    </header>

    <div class="mb-5">
      <p class="leading-relaxed text-slate-600">
        {{ $t('widget.related_products.description') }}
      </p>
    </div>

    <article>
      <div v-if="status === 'pending'">
        <div class="flex gap-4">
          <div v-for="i in 3" :key="i" class="w-[90%] flex-shrink-0 sm:w-[47%] lg:w-[31%]">
            <WidgetHomePageBestOffersSkeleton />
          </div>
        </div>
      </div>

      <div v-else-if="!products || !products.length" class="text-center">
        {{ $t('widget.related_products.no_data') }}
      </div>

      <div v-else>
        <Swiper :space-between="16" :slides-per-view="'auto'">
          <SwiperSlide
            v-for="(item, index) in products"
            :key="item.id"
            class="!w-[90%] sm:!w-[47%]"
          >
            <component
              :is="getPrimaryCardComponent(item.related_type)"
              v-bind="mapItemByType(item)"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </article>
  </section>
</template>
